import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  InputBase,
  IconButton,
  CircularProgress,
  Fade,
  Paper,
} from "@mui/material";
import { SearchRounded, CloseRounded } from "@mui/icons-material";
import MethodGet from "../../config/Service";
import CardProduct from "../../components/Products/CardProduct";

const SearchProducts = ({ results, setResults, onLoadingChange }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleClear = () => {
    setSearchTerm("");
    setDebouncedTerm("");
    setResults([]);
  };

  const handleSearch = () => {
    if (searchTerm.trim()) {
      setDebouncedTerm(searchTerm);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Debounce
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm.trim().length >= 2) {
        setDebouncedTerm(searchTerm);
      } else if (searchTerm === "") {
        setResults([]);
      }
    }, 600);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // API call
  useEffect(() => {
    const fetchData = async () => {
      if (!debouncedTerm) {
        return;
      }
      setLoading(true);
      if (onLoadingChange) onLoadingChange(true);
      try {
        let url = `/distribucion/p/search?q=${debouncedTerm}`;
        const response = await MethodGet(url);
        setResults(response.data);
      } catch (error) {
        console.error("Error al realizar la búsqueda:", error);
        setResults([]);
      } finally {
        setLoading(false);
        if (onLoadingChange) onLoadingChange(false);
      }
    };

    fetchData();
  }, [debouncedTerm]);

  return (
    <Box>
      {/* Search Bar */}
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          alignItems: "center",
          borderRadius: "16px",
          backgroundColor: isFocused ? "#fff" : "#faf5f7",
          border: isFocused ? "2px solid #801F56" : "2px solid #f0dce4",
          boxShadow: isFocused
            ? "0 4px 20px rgba(128, 31, 86, 0.12)"
            : "0 2px 8px rgba(0,0,0,0.04)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          px: 2.5,
          py: 1,
          "&:hover": {
            backgroundColor: "#fff",
            borderColor: "#d4a0b9",
            boxShadow: "0 4px 16px rgba(128, 31, 86, 0.08)",
          },
        }}
      >
        {/* Search Icon */}
        <IconButton
          onClick={handleSearch}
          sx={{
            color: isFocused ? "#801F56" : "#999",
            p: 1,
            transition: "color 0.2s ease",
            "&:hover": {
              backgroundColor: "transparent",
              color: "#801F56",
            },
          }}
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: "#801F56" }} />
          ) : (
            <SearchRounded sx={{ fontSize: 24 }} />
          )}
        </IconButton>

        {/* Input */}
        <InputBase
          fullWidth
          placeholder="Buscar productos..."
          value={searchTerm}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          sx={{
            ml: 1,
            flex: 1,
            fontSize: "1.05rem",
            fontWeight: 500,
            color: "#333",
            "& input::placeholder": {
              color: "#aaa",
              opacity: 1,
              fontWeight: 400,
            },
          }}
        />

        {/* Clear Button */}
        {searchTerm && (
          <IconButton
            onClick={handleClear}
            sx={{
              color: "#bbb",
              p: 1,
              transition: "all 0.2s ease",
              "&:hover": {
                color: "#801F56",
                backgroundColor: "rgba(128, 31, 86, 0.08)",
              },
            }}
          >
            <CloseRounded sx={{ fontSize: 20 }} />
          </IconButton>
        )}
      </Paper>

      {/* Search Results */}
      {(results.length > 0 || (loading && debouncedTerm)) && (
        <Fade in={true} timeout={300}>
          <Box sx={{ mt: 4 }}>
            {/* Results Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                mb: 3,
                pb: 2,
                borderBottom: "1px solid #e5e5e5",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 800,
                    color: "#111",
                    letterSpacing: "-0.02em",
                  }}
                >
                  Resultados de búsqueda
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.9rem",
                    color: "#707072",
                    mt: 0.5,
                  }}
                >
                  {loading ? "Buscando..." : `${results.length} productos encontrados para "${debouncedTerm}"`}
                </Typography>
              </Box>

              {/* Close Results */}
              <IconButton
                onClick={handleClear}
                sx={{
                  color: "#707072",
                  border: "1.5px solid #e5e5e5",
                  borderRadius: "50%",
                  p: 1,
                  "&:hover": {
                    color: "#111",
                    borderColor: "#111",
                    backgroundColor: "transparent",
                  },
                }}
              >
                <CloseRounded sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* Loading State */}
            {loading && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 8,
                }}
              >
                <Box sx={{ textAlign: "center" }}>
                  <CircularProgress
                    size={32}
                    thickness={3}
                    sx={{ color: "#111" }}
                  />
                  <Typography
                    sx={{
                      mt: 2,
                      fontSize: "0.9rem",
                      color: "#707072",
                      fontWeight: 500,
                    }}
                  >
                    Buscando productos...
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Results Grid */}
            {!loading && results.length > 0 && (
              <Grid container spacing={2}>
                {results.map((result, index) => (
                  <Grid
                    item
                    xs={6}
                    sm={4}
                    md={3}
                    lg={2.4}
                    key={result.id || index}
                    sx={{
                      animation: `fadeInUp 0.3s ease ${index * 0.03}s both`,
                      "@keyframes fadeInUp": {
                        from: {
                          opacity: 0,
                          transform: "translateY(10px)",
                        },
                        to: {
                          opacity: 1,
                          transform: "translateY(0)",
                        },
                      },
                    }}
                  >
                    <CardProduct product={result} />
                  </Grid>
                ))}
              </Grid>
            )}

            {/* No Results State */}
            {!loading && debouncedTerm && results.length === 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 8,
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    backgroundColor: "#f5f5f5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2,
                  }}
                >
                  <SearchRounded sx={{ fontSize: 28, color: "#707072" }} />
                </Box>
                <Typography
                  sx={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    color: "#111",
                    mb: 1,
                  }}
                >
                  No encontramos resultados
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.9rem",
                    color: "#707072",
                    maxWidth: 300,
                  }}
                >
                  Intenta con otro término de búsqueda o revisa la ortografía
                </Typography>
              </Box>
            )}
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default SearchProducts;
